<template>
  <div class="flex flex-col w-full mb-4 gap-y-4 overflow-x-auto p-2">
    <ais-refinement-list :attribute="attribute" :transform-items="transformedItems">
      <template v-slot="{ refine }">
        <div class="grid grid-cols-6 gap-x-6">
          <div v-for="(statusItem, index) in schema">
            <a :key="statusItem.label" @click.prevent="refineResults(refine, statusItem)">
            <dashboard-card
              :number="refinementCount(statusItem.label)"
              :description="statusItem.label"
              :class-format="`${
                isSelected(statusItem.label) ? 'bg-blue-400' : 'bg-white'
              } min-w-[12.5rem] shadow-lg rounded-lg p-4 px-6`"
              :data-test-id="`card-${index}`"
            >
              <template v-if="iconLookupFunction" #icon>
                <component :is="iconLookupFunction(statusItem.label)?.value" />
              </template>
            </dashboard-card>
          </a>
          </div>
          <a :key="ALL_LABEL" @click.prevent="clearRefinements(refine)">
            <dashboard-card
              :number="totalOrders"
              :description="ALL_LABEL"
              :class-format="`${
                isSelected(ALL_LABEL) ? 'bg-blue-400' : 'bg-white'
              } min-w-[12.5rem] shadow-lg rounded-lg p-4 px-6`"
              :data-test-id="`card-5`"
            >
              <template v-if="iconLookupFunction" #icon>
                <component :is="iconLookupFunction(ALL_LABEL)?.value" />
              </template>
            </dashboard-card>
          </a>
        </div>
      </template>
    </ais-refinement-list>
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import { AisRefinementList } from "vue-instantsearch/vue3/es";
import { useRoute } from "vue-router";
import DashboardCard from "../Display/DashboardCard.vue";

const ALL_LABEL = "All";

interface Facet {
  label: string;
}

interface FacetItem extends Facet {
  count: number;
}

const emit = defineEmits<(e: "algolia-updated") => void>();

const props = defineProps({
  page: {
    type: String,
    default: "listing",
  },
  attribute: {
    type: String,
    default: "",
  },
  queryAttribute: {
    type: String,
    default: "",
  },
  schema: {
    type: Array<Facet>,
    default: [],
  },
  iconLookupFunction: {
    type: Function,
  },
  attributeDescriptionFunction: {
    type: Function,
  },
  adjustments: {
    type: Object,
    default: {},
  },
});

const route = useRoute();

const countsMap = ref<Map<string, number>>(new Map());
const statusItems = ref<FacetItem[]>([]);

const transformedItems = (items: FacetItem[]): FacetItem[] => {
  const map = new Map<string, number>();
  (items ?? []).forEach((item) => {
    map.set(item.label, item.count);
  });
  countsMap.value = map;
  statusItems.value = items;
  emit("algolia-updated");
  return items;
};

const clearRefinements = (refineFunction: (refinement: string) => void) => {
  [...selectedSet.value].filter((item) => item !== ALL_LABEL).forEach((item) => refineFunction(item));
};

const refineResults = (refineFunction: (refinement: string) => void, facet: Facet) => {
  clearRefinements(refineFunction);
  refineFunction(facet.label);
};

const totalOrders = computed<number>(() => {
  let total: number = 0;
  total += adjustmentCount.value;
  statusItems.value.forEach((item) => {
    total += item.count;
  });
  return total;
});

const adjustmentCount = computed<number>(() => {
  return Object.values(props.adjustments).reduce((a, c) => a + c, 0);
});

const refinementCount = (label: string): number => {
  const adjustment = props.adjustments[label] ?? 0;
  return (countsMap.value.get(label) ?? 0) + adjustment;
};

const selectedSet = computed<Set<string>>(() => {
  const raw = (route.query[props.queryAttribute] as string | undefined) ?? ALL_LABEL;
  return new Set<string>(
    raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map(decodeURIComponent),
  );
});

const isSelected = (label: string) => {
  return selectedSet.value.has(label);
};
</script>
<style scoped>
/* Scrollbar Width */
::-webkit-scrollbar {
  height: 6px;
}

/* Scrollbar Track */
::-webkit-scrollbar-track {
  background-color: #fafbff; /* Tailwind's bg-gray-100 */
}

/* Scrollbar Thumb */
::-webkit-scrollbar-thumb {
  background-color: #7a7a7a; /* Tailwind's bg-blue-500 */
  border-radius: 9999px; /* Tailwind's rounded-full */
}

/* Scrollbar Thumb on Hover */
::-webkit-scrollbar-thumb:hover {
  background-color: #7a7a7a; /* Tailwind's bg-blue-700 */
}
</style>
